import React from 'react';
import { JOURNEY_STEPS, getStatusMeta, getStatusIndex } from '../../constants/partnerStatuses';

// ─────────────────────────────────────────────────────────────────────────────
// BorrowerJourney — a vertical status timeline for a DoorlyPro buyer.
//
// Driven entirely by frontend/src/constants/partnerStatuses.js (no hardcoded
// copy/colors). Renders the linear happy-path JOURNEY_STEPS as a vertical
// line-and-dots timeline, matching the inline style used elsewhere in DoorlyPro.
//
// Props:
//   currentStatus — a partner_status label (OPTIONAL). Three modes:
//     • a linear label  → stepper: completed / current / upcoming.
//     • 'Not Moving Forward' (getStatusIndex === -1) → off-ramp: no step marked
//       current, whole timeline muted, with a distinct banner above it.
//     • null / undefined → REFERENCE mode: every step expanded (definition +
//       next step + note), nothing marked current — a full-journey explainer.
//   statusHistory — optional array of { label, date } (ISO date strings) from the
//           backend. When present, a COMPLETED step whose label has an entry
//           shows that date. Coverage is uneven by design: a completed step with
//           no entry renders as a normal completed step, just without a date.
//   title — optional card heading (default 'Borrower Journey'; pass null to omit,
//           e.g. when the page already supplies its own heading).
// ─────────────────────────────────────────────────────────────────────────────

const BRAND = '#3a6ad9';
const DOORLY_AMBER = '#f59e0b';

// Format an ISO date as "Jun 12". Returns null for missing/unparseable input so
// a step never renders "Invalid Date" — it just shows no date.
function formatStepDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function StepDot({ state, owner }) {
  // completed → filled brand dot with a check; current/expanded → highlighted
  // (amber ring for the Doorly-owned step); upcoming/muted → soft gray.
  let bg = '#e5e7eb';
  let border = '#d1d5db';
  let ring = null;
  let check = false;

  if (state === 'completed') {
    bg = BRAND; border = BRAND; check = true;
  } else if (state === 'current' || state === 'expanded') {
    if (owner === 'doorly') { bg = '#ffdd86'; border = DOORLY_AMBER; ring = 'rgba(245,158,11,0.25)'; }
    else if (owner === 'terminal') { bg = '#111827'; border = '#111827'; }
    else { bg = BRAND; border = BRAND; ring = 'rgba(58,106,217,0.22)'; }
  }

  return (
    <div style={{
      position: 'absolute', left: -24, top: 3,
      width: 15, height: 15, borderRadius: '50%',
      backgroundColor: bg, border: `2px solid ${border}`,
      boxShadow: ring ? `0 0 0 4px ${ring}` : 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {check && (
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </div>
  );
}

// ── 🚨 THE PER-TYPE NEXT STEP WAS SELECTED BY NOTHING ───────────────────────
//
// getStatusMeta takes a partnerType and partnerStatuses carries byPartnerType
// overrides for it — and no caller in the app passed one. The only thing that
// ever did was a status pill on the dashboard, which renders the label and the
// colour, neither of which an override changes. So every place that actually
// shows `nextStep` has been showing the SHARED copy.
//
// ⚠️ AND THE SHARED COPY FAILS IN THE DANGEROUS DIRECTION on exactly the status
// the overrides exist for. partnerStatuses' own header says it: without the
// realtor override, home selection reads as "wait on the buyer" — work that is
// theirs under the agreement and a condition of their fee. "A fallback that
// fails toward 'do nothing' is worse than one that fails loudly."
export default function BorrowerJourney({ currentStatus, statusHistory, title = 'Borrower Journey', partnerType }) {
  const hasStatus = currentStatus !== null && currentStatus !== undefined;
  const currentIndex = hasStatus ? getStatusIndex(currentStatus) : null;
  const mode = !hasStatus ? 'reference' : (currentIndex === -1 ? 'offramp' : 'active');
  const offMeta = mode === 'offramp' ? getStatusMeta(currentStatus, partnerType) : null;

  // Earliest date per partner label (first entry wins; backend already returns
  // one dated entry per label). Empty when no history is supplied.
  const historyByLabel = {};
  if (Array.isArray(statusHistory)) {
    for (const h of statusHistory) {
      if (h && h.label && historyByLabel[h.label] === undefined) historyByLabel[h.label] = h.date;
    }
  }

  return (
    <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24 }}>
      {title && (
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>{title}</h3>
      )}

      {/* Off-ramp banner — 'Not Moving Forward' is a state, not a step. */}
      {mode === 'offramp' && offMeta && (
        <div style={{
          background: '#fff0f3', border: `1px solid ${offMeta.color.bg}`, borderLeft: `4px solid ${offMeta.color.bg}`,
          borderRadius: 10, padding: '12px 14px', marginBottom: 18,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: offMeta.color.bg }}>{offMeta.label}</div>
          <div style={{ fontSize: 12.5, color: '#374151', marginTop: 4, lineHeight: 1.5 }}>{offMeta.definition}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, lineHeight: 1.5 }}>{offMeta.nextStep}</div>
        </div>
      )}

      <div style={{ position: 'relative', paddingLeft: 24 }}>
        {/* Vertical line */}
        <div style={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 2, backgroundColor: '#e5e7eb' }} />

        {JOURNEY_STEPS.map((label, idx) => {
          const meta = getStatusMeta(label, partnerType);
          let state;
          if (mode === 'reference') state = 'expanded';
          else if (mode === 'offramp') state = 'muted';
          else state = idx < currentIndex ? 'completed' : idx === currentIndex ? 'current' : 'upcoming';

          const showDetail = state === 'current' || state === 'expanded';
          const isDoorly = meta.owner === 'doorly';
          const labelColor = state === 'current' || state === 'expanded'
            ? '#111827'
            : state === 'completed' ? '#6b7280' : '#9ca3af';
          // Date only on completed steps that have a history entry. Undated
          // completed steps are normal — they simply render without a date.
          const stepDate = state === 'completed' ? formatStepDate(historyByLabel[meta.label]) : null;

          return (
            <div key={meta.key} style={{ position: 'relative', marginBottom: idx < JOURNEY_STEPS.length - 1 ? 18 : 0 }}>
              <StepDot state={state} owner={meta.owner} />

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: state === 'current' ? 700 : 600, color: labelColor }}>
                  {meta.label}
                </span>
                {isDoorly && showDetail && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: '#92400e', background: '#fffbeb',
                    border: `1px solid ${DOORLY_AMBER}`, borderRadius: 20, padding: '1px 8px',
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>With Doorly</span>
                )}
                {state === 'current' && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: BRAND, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    ● Current
                  </span>
                )}
                {stepDate && (
                  <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>{stepDate}</span>
                )}
              </div>

              {showDetail && (
                <div style={{ marginTop: 4 }}>
                  <div style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.5 }}>{meta.definition}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3, lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 700, color: isDoorly ? DOORLY_AMBER : BRAND }}>What's next: </span>
                    {meta.nextStep}
                  </div>
                  {meta.note && (
                    <div style={{
                      marginTop: 8, fontSize: 12, color: '#92400e', lineHeight: 1.5,
                      background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px',
                    }}>
                      {meta.note}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
